// Full literal class strings, one per bucket — Tailwind's content scanner only picks up classes
// that appear verbatim in scanned source text, so a runtime-built string like
// `bg-avatar-${index}-bg` would never compile: none of `bg-avatar-1-bg` … `bg-avatar-8-bg` would
// actually exist in the source Tailwind scans, only the template pieces. This array is what makes
// all 8 combinations real, present strings.
const AVATAR_COLOR_CLASSES = [
  'bg-avatar-1-bg text-avatar-1-fg',
  'bg-avatar-2-bg text-avatar-2-fg',
  'bg-avatar-3-bg text-avatar-3-fg',
  'bg-avatar-4-bg text-avatar-4-fg',
  'bg-avatar-5-bg text-avatar-5-fg',
  'bg-avatar-6-bg text-avatar-6-fg',
  'bg-avatar-7-bg text-avatar-7-fg',
  'bg-avatar-8-bg text-avatar-8-fg',
] as const;

/**
 * Deterministic bucket in `[0, AVATAR_COLOR_CLASSES.length)` for a person, from a stable seed
 * (their id when the caller has one, their display name otherwise). Same reasoning as
 * `initials()` being a pure function of the name: two renders, two pages, or a second list
 * containing the same person must all land on the same colour — a `Math.random()` pick would
 * flicker between renders and disagree between a kudos card and a team table showing the same
 * person.
 *
 * A small FNV-1a-style hash, not `crypto` — this only needs to be stable and roughly evenly
 * distributed across the palette, not collision-resistant.
 */
export function avatarColorIndex(seed: string): number {
  let hash = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    hash ^= seed.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return Math.abs(hash) % AVATAR_COLOR_CLASSES.length;
}

/** Token-reference class names (background + foreground) for one avatar colour bucket — see
 *  `tokens.css`'s `--avatar-*-bg`/`--avatar-*-fg` pairs (8 hues, light/dark-mode aware). */
export function avatarColorClasses(seed: string): string {
  return AVATAR_COLOR_CLASSES[avatarColorIndex(seed)]!;
}
